import {
  FACET_KEYS,
  FACET_VALUES,
  PRICE_FACET,
  PRICE_FILTER,
  PROMO_FILTER,
  SINGLE_FILTERS,
  SORTBADGE_EARLY_ACCESS,
  SORTBADGE_GUEST,
} from '@/scripts/constants/plp'
import { AllFacets, AppliedFacets, Entry, Facet, Facets, SortOption } from '@/types/plp'
import { AlgoliaFacets, AlgoliaProductHit, PriceRange } from '@/scripts/libs/algolia/types/plp'
import Algolia, { AlgoliaTranslations, OptionsObject, SearchResponse } from '@/scripts/libs/algolia'
import AlgoliaPromoToggles from '@/scripts/libs/algolia/promo-toggles'
import { NoEcommerce } from '@/scripts/libs/no-ecommerce'
import { NEGATIVE_VALUES, POSITIVE_VALUES, TRUE, Y } from '@/scripts/constants/booleans'
import { ALL_PROMOS, ALL_PROMOS_LABEL, FACET_KEY_PROMO_LABEL } from '@/scripts/constants/promos'
import { SearchOptions } from '@algolia/client-search'
import makeDebug from 'debug'
import { ALGOLIA_FACETS, ENCODED_COLON } from '@/scripts/constants/algolia'
import wcs from '@/scripts/libs/wcs'
import { SEARCH_TERM_QUERY_PARAM } from '@/scripts/constants/search'
import { WcsUser } from '@/scripts/libs/wcs/user'
import { WcsPromotions } from '@/scripts/libs/wcs/promotions'
import PromoToggles from '@/scripts/libs/promo-toggles'
import I18n from '@/scripts/libs/i18n'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const debug = makeDebug('algolia:facets')
class AlgoliaFacetsUtils {
  private facetConf = {
    [FACET_KEYS.PRESCRIPTION_AVAILABLE]: { show: this.showPrescription },
    [FACET_KEYS.POLARIZED]: { show: this.showPolarized },
    [FACET_KEYS.HIGH_PRESCRIPTION]: { show: () => true },
    [FACET_KEYS.PRICE]: { show: () => this.showPriceFacet() },
    [FACET_KEYS.POPULAR_FILTERS]: { show: () => true },
    [FACET_KEYS.PRICEFILTER_GUEST]: { show: () => true },
    [FACET_KEYS.PROMO_FILTER]: { show: () => this.showPromofilter() },
  }
  translations: AlgoliaTranslations | undefined = undefined
  private commonFacetConf = {
    show: this.commonShowFacets,
  }
  private espotAlogliaPromo = {
    PromoFiltersDisplayEnabled: false,
  }
  constructor() {
    ;(async () => {
      await this.init()
    })()
  }
  private async init() {
    await this.getAlgoliaPromoToggles()
  }

  fetchFiltersTranslations = async (): Promise<void> => {
    if (this.translations) {
      return
    }
    const { locale } = wcs.getStoreConfig()
    try {
      const translations = I18n.getInstance()?.getLocaleMessage(locale)
      this.translations = (translations?.plp || ({} as any))?.filters
    } catch {
      try {
        const resp = (await I18n.loadTranslations(locale)) as any
        this.translations = resp?.plp?.filters
      } catch {
        this.translations = undefined
      }
    }
  }

  mergeFacetsForAlgoliaSearch = (facets: Array<string>): Array<string | Array<string>> => {
    let output: Array<string | Array<string>> = []
    const map: Record<string, string | Array<string>> = {}

    const facetsWithoutPriceAndPromoTogglesOrHingeDistance = facets.filter(
      (facet) =>
        !facet.includes(PRICE_FILTER) &&
        !facet.includes(PROMO_FILTER) &&
        !facet.includes('hingeDistance')
    )

    const sanitizedFacets = facetsWithoutPriceAndPromoTogglesOrHingeDistance
      .map((facet) => {
        if (facet.includes(FACET_KEYS.GENDER)) {
          return facet.split(',')
        }
        return facet
      })
      .flatMap((item) => item)

    for (const facet of sanitizedFacets) {
      const [key] = facet.split(':')
      if (map[key]) {
        if (Array.isArray(map[key])) {
          map[key] = [...map[key], facet]
        } else {
          map[key] = [map[key] as string, facet]
        }
      } else {
        map[key] = facet
      }
    }

    for (const key in map) {
      output = [...output, map[key]]
    }

    const promoFilters = facets.filter((facet) => facet.includes(PROMO_FILTER))

    if (promoFilters.length) {
      output = [...output, promoFilters]
    }

    return output
  }

  getCategoryFacet = (): undefined | string => {
    const queryParams = new URLSearchParams(window.location.search)
    const searchTermQueryParam = queryParams.get(SEARCH_TERM_QUERY_PARAM) ?? undefined
    if (searchTermQueryParam) {
      return ''
    }

    const category = (document.getElementById('pageType') as HTMLInputElement)?.value

    if (!category || category.includes('SearchResults')) {
      return ''
    }

    return `categories:${category}`
  }

  formatPriceFilter = (rawPriceFilter: string): Array<string> => {
    const formattedPriceFilter = rawPriceFilter
      .replace('xBestPrice:', '')
      .replace('[', '')
      .replace(']', '')

    return formattedPriceFilter.split(ALGOLIA_FACETS.TO)
  }

  applyPriceFilter = (
    appliedFacets: AppliedFacets,
    fallbackMax?: number | string
  ): Array<string> => {
    if (!appliedFacets) return []

    const priceFilter = appliedFacets.find((facet) => facet.includes(PRICE_FILTER))
    if (!priceFilter) return []

    const [min, max] = this.formatPriceFilter(priceFilter)

    let result = [`${ALGOLIA_FACETS.SORT_PRICE_GUEST}>=${min}`]

    if (max !== '*') {
      result = [...result, `${ALGOLIA_FACETS.SORT_PRICE_GUEST}<=${max}`]
    } else if (fallbackMax !== undefined) {
      result = [...result, `${ALGOLIA_FACETS.SORT_PRICE_GUEST}<=${fallbackMax}`]
    }

    return result
  }

  mapPriceRange(allFacets: Facets): PriceRange {
    const priceFacetValues = allFacets?.facets?.PRICE?.entry
    return {
      min: priceFacetValues?.find((value) => value.label === ALGOLIA_FACETS.MIN)?.entryValue ?? '',
      max: priceFacetValues?.find((en) => en.label === ALGOLIA_FACETS.MAX)?.entryValue ?? '',
    }
  }
  mergeOriginalAndNewFacets(conf: {
    originalFacets: undefined | Facets
    newFacets: Facets
    promoFiltersApplied?: {
      sortBadge_Guest?: { [k: string]: number }
      sortBadge_early_access?: { [k: string]: number }
    }
  }): Facets {
    const { originalFacets, newFacets, promoFiltersApplied } = conf
    if (!originalFacets) return {} as Facets
    const originalFacetsEntries = Object.entries(originalFacets.facets)
    const newMappedFacets = originalFacetsEntries.reduce((accumulator, [key, value]) => {
      const newFacetEntry = newFacets.facets[key]
      if (!newFacetEntry) {
        accumulator[key] = Object.assign({}, value, {
          entry: value?.entry?.map((e) => ({ ...e, count: '0' })),
        })
      } else {
        const objectToReturn = {
          entry: value?.entry?.map((entryValue) => {
            const { identifier, key: entryValueKey, label } = entryValue
            let countToReturn: string | number
            if (key === FACET_KEYS.PRICE) {
              const newPrice = newFacetEntry.entry.find((entry) => entry.label === label)
              return { ...newPrice }
            }
            const newEntry = newFacetEntry?.entry?.find((newFacetE) => {
              if (key === FACET_KEYS.MODEL_SIZE_DISPLAY && !newFacetE.identifier) {
                return newFacetE.key === entryValueKey
              }
              return newFacetE.identifier === identifier
            })
            countToReturn = newEntry ? (newEntry.count as string) : '0'

            if (key === FACET_KEYS.PROMO_FILTER) {
              const isUserEligibleForEarlyAccess =
                WcsUser.isEarlyAccessActivityInUserClusterCookie()
              const currentPromoActiveType = isUserEligibleForEarlyAccess
                ? SORTBADGE_EARLY_ACCESS
                : SORTBADGE_GUEST
              const currentPromoActive = (promoFiltersApplied || {})[currentPromoActiveType] || {}
              countToReturn = currentPromoActive[identifier || '']
              if (identifier === ALL_PROMOS) {
                countToReturn = Object.entries(currentPromoActive).reduce(
                  (acc, [, v]) => acc + v,
                  0
                )
              }
            }
            return Object.assign({}, entryValue, {
              count: countToReturn,
            })
          }),
        }
        accumulator[key] = Object.assign({}, value, objectToReturn)
      }
      return accumulator
    }, {} as AllFacets)
    return Object.assign({}, newFacets, {
      facets: newMappedFacets,
    })
  }

  adjustCountOfSelectedFacets = ({
    facets,
    facetsWithAdjustedCount,
  }: {
    facets: Facets
    facetsWithAdjustedCount: Array<Facets>
  }): Facets => {
    const result: Facets = { ...facets }
    const excludedFilterIfNotFound = [FACET_KEYS.POPULAR_FILTERS]
    facetsWithAdjustedCount.forEach((facetWithAdjustedCount) => {
      Object.entries(facetWithAdjustedCount?.facets).forEach(
        ([productDetailKey, productDetailValue]) => {
          if (productDetailKey !== FACET_KEYS.PRICE) {
            result.facets[productDetailKey] = {
              ...result.facets[productDetailKey],
              entry:
                result.facets[productDetailKey]?.entry.map((entry) => {
                  const found = productDetailValue.entry.find((newEntry) => {
                    if (
                      productDetailKey === FACET_KEYS.MODEL_SIZE_DISPLAY &&
                      !newEntry.identifier
                    ) {
                      return newEntry.key === entry.key
                    }
                    return newEntry.identifier === entry.identifier
                  })
                  if (excludedFilterIfNotFound.includes(productDetailKey) && !found) {
                    return entry
                  }

                  if (found) {
                    return {
                      ...entry,
                      count: found.count,
                    }
                  } else {
                    return {
                      ...entry,
                      count: '0',
                    }
                  }
                }) ?? [],
            }
          }
        }
      )
    })
    return result
  }

  disableSiblingsOfSelectedFilters = ({
    facets,
    appliedFacets,
  }: {
    facets: Facets
    appliedFacets: AppliedFacets
  }): Facets => {
    if (!facets) return {} as Facets

    if (!appliedFacets) {
      return facets
    }

    const facetsEntries = Object.entries(facets.facets)

    const result: Facets = { ...facets }

    const { plp } = wcs.getStoreConfig()

    facetsEntries.forEach(([key]) => {
      result.facets[key] = {
        ...result.facets[key],
        entry: result.facets[key]?.entry?.map((e) => {
          const entryIsSelected = e.entryValue && appliedFacets.includes(e.entryValue)
          const entryCategoryIsDisabled = plp.facetsWithExclusiveBehaviour.includes(key)

          const siblings = result.facets[key].entry.filter(
            (entry) => entry.entryValue !== e.entryValue
          )

          const siblingIsSelected = siblings.some(
            (sibling) => sibling.entryValue && appliedFacets.includes(sibling.entryValue)
          )

          const facetIsDisabled = !entryIsSelected && entryCategoryIsDisabled && siblingIsSelected

          if (facetIsDisabled) {
            return { ...e, count: '0' }
          } else {
            return { ...e }
          }
        }),
      }
    })

    return result
  }

  mapPriceFacet = ({
    facetStats,
    appliedFacets,
  }: {
    facetStats?: Record<string, { min: number; max: number; avg: number; sum: number }>
    appliedFacets?: AppliedFacets
    priceRange: PriceRange | undefined
  }): Facet => {
    const label = this.getEntryTranslation(FACET_KEYS.PRICE)?.label ?? FACET_KEYS.PRICE
    let min: string | number = facetStats?.sortPrice_Guest?.min || '0'
    let max: string | number = facetStats?.sortPrice_Guest?.max || '0'
    const findAppliedFacetPrice = appliedFacets?.find((af) => af.includes(PRICE_FILTER))
    if (findAppliedFacetPrice) {
      const [minPrice, maxPrice] = this.formatPriceFilter(findAppliedFacetPrice)
      min = minPrice
      max = maxPrice
    }
    return {
      name: label,
      entry: [
        {
          count: '1',
          label: ALGOLIA_FACETS.MIN,
          entryValue: min as string,
        },
        {
          count: '1',
          label: ALGOLIA_FACETS.MAX,
          entryValue: max as string,
        },
      ],
    }
  }

  getFacets = async ({
    promoFilterIsVisible,
    searchResponse,
    appliedFacets,
    appliedOrderBy,
    priceRange,
  }: {
    promoFilterIsVisible?: boolean
    searchResponse: undefined | SearchResponse<AlgoliaProductHit>
    appliedOrderBy?: string
    appliedFacets?: AppliedFacets
    priceRange?: PriceRange
  }): Promise<Facets> => {
    if (!searchResponse || !searchResponse.facets) return {} as Facets
    const { facets: algoliaFacets } = searchResponse
    const facets = this.mapFacets(algoliaFacets)
    const facetsWithPopularFilters = this.mergePopularFilters(facets)
    facetsWithPopularFilters.PRICE = this.mapPriceFacet({
      facetStats: searchResponse.facets_stats,
      appliedFacets,
      priceRange,
    })
    const facetsNamesInOrder = searchResponse.renderingContent?.facetOrdering?.facets?.order

    const facetsValuesInOrder = searchResponse.renderingContent?.facetOrdering?.values

    if (promoFilterIsVisible) {
      facetsWithPopularFilters.PROMO_FILTER = await this.mapPromoFacets(searchResponse)
    }
    const sortedFacets = this.sortFacets({
      currentFacets: facetsWithPopularFilters,
      orderOfFacets: facetsNamesInOrder,
      orderOfValuesPerFacet: facetsValuesInOrder,
    })

    const mergedFacets = this.mergeGenderFacets(sortedFacets)

    const mappedAppliedFacets = this.mapAppliedFacets({
      facets: algoliaFacets,
      appliedFacets,
      priceRange,
    })

    const mergedAppliedFacetsForRecap = this.mergeGenderFacetsForRecap(mappedAppliedFacets)
    return {
      appliedFacets: mergedAppliedFacetsForRecap,
      sortOptions: this.mapSortOptions(appliedOrderBy),
      facets: mergedFacets,
      totalCount: `${searchResponse.nbHits}`,
    }
  }

  private getEntryTranslation = (formattedKey: string): any => {
    return (
      this.translations &&
      Object.entries(this.translations?.facets).find(([key]) => key === formattedKey)?.[1]
    )
  }

  private getTranslatedDescription = (formattedKey: string, entryKey: string) => {
    const entryKeyToCheck = entryKey?.includes('|') ? entryKey?.split('|')[1] : entryKey
    const translatedEntry = this.getEntryTranslation(formattedKey)
    if (!translatedEntry?.options) {
      return ''
    }
    const entries = Object.entries(translatedEntry.options)
    const findDescription =
      entries?.find(([key]) => {
        return key === entryKeyToCheck || POSITIVE_VALUES.includes(entryKeyToCheck)
      }) || []

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return findDescription.length ? findDescription[1].description : ''
  }

  private getTranslatedSortBy = (optionIdentifier: string) => {
    if (!this.translations?.sort_by) {
      return ''
    }

    const sortBy = Object.entries(this.translations.sort_by).find(([key]) => key === 'options')?.[1]
    if (!sortBy) {
      return ''
    }
    const option = Object.entries(sortBy).find(([key]) => key === optionIdentifier)?.[1]
    return option?.label ?? ''
  }

  private mapFacets(facets: Record<string, Record<string, number>>): Record<string, Facet> {
    const facetsEntries = Object.entries(facets).filter(
      ([key]) => key.includes('attributes_translated.') || key.includes('priceFilter_Guest')
    )
    const result: Record<string, Facet> = {}
    facetsEntries.forEach(([key, value]) => {
      const formattedKey = key.replace('attributes_translated.', '')

      const facetOfFormattedKey = facetsEntries.find(([key]) => key.includes(formattedKey))

      const translatedEntry = this.getEntryTranslation(formattedKey)

      result[formattedKey] = {
        name: this.mapFacetName({ formattedKey, label: translatedEntry?.label }),
        entry: Object.entries(value)
          .filter(([key]) => {
            const values = Object.keys(value)
            if (values.some((key) => POSITIVE_VALUES.includes(key))) {
              return POSITIVE_VALUES.includes(key)
            }
            return !values.some((key) => NEGATIVE_VALUES.includes(key))
          })
          .map(([entryKey, entryVal]) => {
            let label = facetOfFormattedKey
              ? Object.keys(facetOfFormattedKey[1])
                  .find((option) => option.includes(entryKey))
                  ?.split('|')[0]
              : entryKey

            if (POSITIVE_VALUES.includes(entryKey) && translatedEntry) {
              label = translatedEntry.label
            }

            if (
              !POSITIVE_VALUES.includes(entryKey) &&
              !NEGATIVE_VALUES.includes(entryKey) &&
              translatedEntry?.options &&
              Algolia.isOptionsObject(translatedEntry.options)
            ) {
              label = this.mapLabelForFacetWithOptions(entryKey, translatedEntry.options) ?? label
            }

            const facetDescription = this.getTranslatedDescription(formattedKey, entryKey)

            const facetConf = this.getFacetConf(formattedKey) || {}
            const identifier = entryKey.split('|')?.[1] ?? entryKey
            return {
              key: identifier,
              count: String(entryVal),
              label,
              facetDesc: facetDescription,
              showFacet: facetConf?.show(undefined),
              entryValue: `${key}:${entryKey}`,
              identifier: this.getFacetIdentifier(formattedKey, identifier),
            }
          }),
      }
    })
    return result
  }

  private getFacetIdentifier(formattedKey: string, identifier: string) {
    if (['POLARIZED', 'PRESCRIPTION_AVAILABLE', 'CUSTOMIZABLE'].includes(formattedKey)) {
      return formattedKey
    }
    return formattedKey !== 'MODEL_SIZE_DISPLAY' ? identifier : ''
  }

  private mapLabelForFacetWithOptions(entryKey: string, translatedKeyOptions: OptionsObject) {
    const splitEntryKey = entryKey?.includes('|') ? entryKey.split('|')[1] : entryKey
    const optionWithEntryKey = Object.entries(translatedKeyOptions)?.find(
      ([key]) => key === splitEntryKey
    )?.[1]
    const label = optionWithEntryKey?.label

    return label
  }

  private mergeGenderFacets(facets: Record<string, Facet>) {
    const genderFacet = facets[FACET_KEYS.GENDER]
    if (!genderFacet) {
      return facets
    }

    const unisexEntry = genderFacet.entry?.find((entry) => entry.identifier === FACET_VALUES.UNISEX)
    if (!unisexEntry) {
      return facets
    }

    const mergedFacet = genderFacet.entry
      ?.filter((entry) => entry.identifier !== FACET_VALUES.UNISEX)
      .map((entry) => ({ ...entry, entryValue: `${entry.entryValue},${unisexEntry.entryValue}` }))
    facets[FACET_KEYS.GENDER] = { ...genderFacet, entry: mergedFacet }
    return facets
  }

  private mergePopularFilters(facets: Record<string, Facet>): Record<string, Facet> {
    const { POLARIZED, PRESCRIPTION_AVAILABLE, NEWRELEASES, CUSTOMIZABLE, ...restFacets } = facets

    const popularFiltersTranslation = this.getEntryTranslation(FACET_KEYS.POPULAR_FILTERS)

    const entry = []

    if (NEWRELEASES?.entry.length > 0) {
      entry.push({
        ...NEWRELEASES?.entry[0],
        label: this.getEntryTranslation(FACET_KEYS.NEWRELEASES)?.label ?? 'New Arrivals',
      })
    }

    if (POLARIZED?.entry.length > 0) {
      entry.push({
        ...POLARIZED?.entry[0],
        label: this.getEntryTranslation(FACET_KEYS.POLARIZED)?.label ?? 'Polarized',
      })
    }

    if (PRESCRIPTION_AVAILABLE?.entry.length > 0) {
      entry.push({
        ...PRESCRIPTION_AVAILABLE?.entry[0],
        label: this.getEntryTranslation(FACET_KEYS.PRESCRIPTION_AVAILABLE)?.label ?? 'Prescription',
      })
    }

    if (CUSTOMIZABLE?.entry.length > 0) {
      entry.push({
        ...CUSTOMIZABLE?.entry[0],
        label: this.getEntryTranslation(FACET_KEYS.CUSTOMIZABLE)?.label ?? 'Customizable',
      })
    }

    return {
      ...restFacets,
      POPULAR_FILTERS: {
        name: popularFiltersTranslation?.label?.toLowerCase() ?? FACET_KEYS.POPULAR_FILTERS,
        entry,
      },
    }
  }

  private mergeGenderFacetsForRecap(facet: Facet): Facet {
    const unisexEntry = facet.entry.find((entry) => entry.identifier === FACET_VALUES.UNISEX)
    if (!unisexEntry) {
      return facet
    }

    const entriesWithoutGender = facet.entry.filter(
      (entry) => !entry.key?.includes(FACET_KEYS.GENDER)
    )

    const genderFacets = facet.entry
      .filter((entry) => entry.identifier !== FACET_VALUES.UNISEX)
      .filter((entry) => entry.key?.includes(FACET_KEYS.GENDER))

    const mergedGenderFacets = genderFacets.map((facet) => ({
      ...facet,
      key: `${facet.key},${unisexEntry.key}`,
    }))

    return {
      ...facet,
      entry: [...entriesWithoutGender, ...mergedGenderFacets],
    }
  }

  private mapFacetName({
    formattedKey,
    label,
  }: {
    formattedKey: string
    label: undefined | string
  }): string {
    if (SINGLE_FILTERS.includes(formattedKey?.toLowerCase())) {
      return formattedKey
    }

    return label ?? formattedKey
  }

  private async mapPromoFacets(searchResponse: SearchResponse<AlgoliaProductHit>): Promise<Facet> {
    const getPromoToggles = await AlgoliaPromoToggles.getPromoToggles(searchResponse)
    const promoToggles = PromoToggles.getTogglesWithUserCluster(getPromoToggles)
    const countSum = promoToggles.reduce((acc, toggle) => acc + (toggle?.count || 0), 0)
    const findAllPromos = promoToggles.findIndex((p) => p.id === ALL_PROMOS)

    if ((typeof !findAllPromos === 'undefined' && !findAllPromos) || findAllPromos === -1) {
      promoToggles.unshift({
        id: ALL_PROMOS,
        entryValue: promoToggles.map((toggle) => toggle.entryValue).flat(),
        badge: ALL_PROMOS_LABEL,
      })
    } else if (findAllPromos > 0) {
      promoToggles.sort((a) => {
        if (a.id === ALL_PROMOS) {
          return -1
        }
        return 1
      })
    }
    return {
      name: FACET_KEY_PROMO_LABEL,
      entry: promoToggles.map((promo) => {
        const firstEntryValue = promo.entryValue[0] || ''
        const isAllPromo = promo.id === ALL_PROMOS
        const identifier = isAllPromo ? ALL_PROMOS : promo.badge
        const entryValue = isAllPromo ? promo.entryValue : firstEntryValue
        const count = isAllPromo ? countSum : promo.count
        let isPromotionInProgress = false
        if (promo.startDateTimestamp && promo.endDateTimestamp) {
          isPromotionInProgress = WcsPromotions.isPromotionInProgress(
            promo.startDateTimestamp || 0,
            promo.endDateTimestamp || 0
          )
        }
        return {
          entryValue: entryValue,
          facetDesc: '',
          identifier: identifier,
          count: count,
          key: entryValue,
          label: promo.badge,
          showFacet: isAllPromo ? promoToggles.length > 2 : isPromotionInProgress,
        }
      }),
      promoFilter: {
        displayAllPromoToggle: 'ALL',
        allPromoLabel: ALL_PROMOS_LABEL,
        pstPriceList: promoToggles.map((promoToggle) => ({
          ...promoToggle,
          command: promoToggle.entryValue?.[0],
        })),
      },
    } as Facet
  }

  mapSortOptions = (appliedOrderBy?: string): Array<SortOption> => {
    return [
      {
        identifier: 'Top Sellers',
        value: null,
      },
      {
        value: '__standard_desc',
        identifier: 'Price (High to Low)',
      },
      {
        value: '__standard_asc',
        identifier: 'Price (Low to High)',
      },
      {
        value: '__new',
        identifier: 'Newest First',
      },
    ].map((option, index) => {
      const label = this.getTranslatedSortBy(option.identifier) ?? ''

      let cssClass = undefined
      if (appliedOrderBy) {
        if (appliedOrderBy === option.value) {
          cssClass = 'selected'
        }
      } else if (index === 0) {
        cssClass = 'selected'
      }

      return {
        ...option,
        cssClass,
        label,
      }
    })
  }

  sortFacets({
    currentFacets,
    orderOfFacets,
    orderOfValuesPerFacet,
  }: {
    currentFacets: Record<string, Facet>
    orderOfFacets?: ReadonlyArray<string>
    orderOfValuesPerFacet?: Record<
      string,
      {
        order?: readonly string[] | undefined
        readonly sortRemainingBy?: 'count' | 'alpha' | 'hidden' | undefined
      }
    >
  }): Record<string, Facet> {
    if (!orderOfFacets) return currentFacets

    const result: Record<string, Facet> = {}

    let promoFilterIsOrdered = false

    orderOfFacets?.forEach((facet) => {
      if (facet.includes('attributes')) {
        const facetName = facet.split('.')[1]
        const originalFacet = currentFacets[facetName]
        if (originalFacet) {
          result[facetName] = this.sortFacetValues({
            facetName,
            facet: originalFacet,
            orderOfValues: orderOfValuesPerFacet,
          })
        }
      }

      if (facet.includes('sortPrice')) {
        result[FACET_KEYS.PRICE] = currentFacets[FACET_KEYS.PRICE]
      }

      if (facet.includes('sortBadge') && !promoFilterIsOrdered) {
        promoFilterIsOrdered = true

        if (currentFacets[FACET_KEYS.PROMO_FILTER]) {
          result[FACET_KEYS.PROMO_FILTER] = currentFacets[FACET_KEYS.PROMO_FILTER]
        }
      }
    })

    const popularFiltersEntryKeys = currentFacets.POPULAR_FILTERS?.entry?.map(
      (entry) => entry.entryValue?.split(':')?.[0]
    )

    const popularFiltersIsPresent = popularFiltersEntryKeys?.some(
      (value) => value && orderOfFacets.includes(value)
    )

    if (popularFiltersIsPresent) {
      const popularFilters = {
        ...currentFacets.POPULAR_FILTERS,
        entry: currentFacets.POPULAR_FILTERS.entry.filter((entry) => {
          const key = entry?.entryValue?.split(':')?.[0]
          return key && orderOfFacets.includes(key)
        }),
      }
      return {
        POPULAR_FILTERS: popularFilters,
        ...result,
      }
    }

    return result
  }

  private sortFacetValues({
    facetName,
    facet,
    orderOfValues,
  }: {
    facetName: string
    facet: Facet
    orderOfValues?: Record<
      string,
      {
        order?: readonly string[] | undefined
        readonly sortRemainingBy?: 'count' | 'alpha' | 'hidden' | undefined
      }
    >
  }): Facet {
    if (!orderOfValues || facet.entry.length === 1) {
      return facet
    }

    const orderEntries = Object.entries(orderOfValues).find(([key]) => key.includes(facetName))

    if (!orderEntries?.[1]) {
      return facet
    }

    const sortRule = orderEntries[1]

    if (sortRule.sortRemainingBy === 'alpha') {
      const entriesWithMatch = facet.entry
        .filter((entry) => entry.label && sortRule?.order?.includes(entry.label))
        .sort((a, b) => {
          if (!sortRule?.order || !a.label || !b.label) {
            return 0
          }
          return sortRule?.order?.indexOf(a.label) - sortRule?.order?.indexOf(b.label)
        })

      const entriesWithoutMatch = facet.entry
        .filter((entry) => entry.label && !sortRule?.order?.includes(entry.label))
        .sort((a, b) => {
          if (a.label && b.label) {
            return a.label.localeCompare(b.label)
          }
          return 0
        })

      return {
        ...facet,
        entry: [...entriesWithMatch, ...entriesWithoutMatch],
      }
    }

    if (sortRule.sortRemainingBy === 'hidden') {
      return {
        ...facet,
        entry: facet.entry
          .filter(
            (entry) =>
              (entry.label && sortRule?.order?.includes(entry.label)) ||
              (entry.key && sortRule?.order?.includes(entry.key))
          )
          .sort((a, b) => {
            if (!sortRule?.order || !a.label || !b.label) {
              return 0
            }
            return sortRule?.order?.indexOf(a.label) - sortRule?.order?.indexOf(b.label)
          }),
      }
    }

    if (sortRule.sortRemainingBy === 'count') {
      return {
        ...facet,
        entry: facet.entry.sort((a, b) => {
          if (!sortRule?.order || !a.count || !b.count) {
            return 0
          }
          return +a.count - +b.count
        }),
      }
    }

    return facet
  }

  mapAppliedFacets = ({
    facets,
    appliedFacets,
    priceRange,
  }: {
    facets: undefined | AlgoliaFacets
    appliedFacets?: AppliedFacets
    priceRange?: PriceRange
  }): Facet => {
    if (!facets || !appliedFacets) return { name: '', entry: [] }
    const facetsEntries = Object.entries(facets).filter(([key]) =>
      key.includes('attributes_translated.')
    )
    return {
      name: '',
      entry: appliedFacets.map((appliedFacet) => {
        const [appliedFacetKey, appliedFacetValue] = appliedFacet.includes(ENCODED_COLON)
          ? appliedFacet.split(ENCODED_COLON)
          : appliedFacet.split(':')
        const formattedKey = appliedFacetKey.replace('attributes.', '')

        const facetOfFormattedKey = facetsEntries.find(([key]) => key.includes(formattedKey))

        const label = facetOfFormattedKey
          ? Object.keys(facetOfFormattedKey[1])
              .find((option) => option.includes(appliedFacetValue))
              ?.split('|')[0]
          : appliedFacetValue

        if (formattedKey === 'hingeDistance') {
          const [min, max] = this.formatHingeDistanceFilter(appliedFacetValue)

          return {
            identifier: 'HINGE_DISTANCE',
            min,
            max,
            label,
            key: appliedFacet,
          }
        }

        if (appliedFacetValue?.includes(' TO ')) {
          const [min, max] = this.formatPriceFilter(appliedFacetValue)
          return {
            identifier: PRICE_FACET,
            min,
            max: max !== '*' ? max : priceRange?.max,
            label,
            key: appliedFacet,
          }
        }

        const translatedfacet = this.getEntryTranslation(formattedKey)

        const translatedFacetLabel = translatedfacet?.label ?? appliedFacetKey.split('.')[1]

        const sanitizedLabel =
          appliedFacetValue === TRUE || appliedFacetValue === Y
            ? translatedFacetLabel
            : appliedFacetValue

        return {
          identifier: appliedFacetValue?.split('|')?.[1] ?? appliedFacetValue,
          label: sanitizedLabel?.replace('\\', '').split('|')?.[0] ?? sanitizedLabel,
          key: appliedFacet,
        }
      }),
    }
  }

  generateQueriesCombinations({
    appliedFacets,
    indexName,
    category,
    searchTerm,
    ruleContexts,
    numericFilters,
    facetingAfterDistinct = false,
  }: {
    appliedFacets: Array<string | Array<string>>
    indexName: undefined | string
    category: undefined | string
    searchTerm: string
    ruleContexts: Array<string>
    numericFilters: Array<string>
    facetingAfterDistinct?: boolean
  }): Array<{ params: SearchOptions; indexName?: string }> {
    return appliedFacets.map((appliedFacet) => {
      const key = this.extractKey(appliedFacet)

      return {
        indexName,
        params: {
          facets: [key],
          facetFilters: this.extractFacetFilters({ appliedFacets, key }),
          filters: category,
          query: searchTerm,
          ruleContexts,
          hitsPerPage: 0,
          numericFilters,
          facetingAfterDistinct,
        },
      }
    })
  }

  private extractKey(appliedFacet: string | string[]) {
    if (Array.isArray(appliedFacet)) {
      const [key] = appliedFacet[0].split(':')
      return key
    } else {
      const [key] = appliedFacet.split(':')
      return key
    }
  }

  private extractFacetFilters({
    appliedFacets,
    key,
  }: {
    appliedFacets: Array<string | Array<string>>
    key: string
  }) {
    const facetFilters = appliedFacets.filter((facetFilter) => {
      if (Array.isArray(facetFilter)) {
        return facetFilter.every((facet: string) => !facet.includes(key))
      } else {
        return !facetFilter.includes(key)
      }
    })

    return facetFilters.length > 0 ? facetFilters : []
  }
  applyHingeDistanceFilter(appliedFacets: AppliedFacets) {
    if (!appliedFacets) return []

    const hingeDistanceFilter = appliedFacets.find((facet) => facet.includes('hingeDistance'))
    if (!hingeDistanceFilter) return []

    const [min, max] = this.formatHingeDistanceFilter(hingeDistanceFilter)

    return [`${ALGOLIA_FACETS.HINGE_DISTANCE}>=${min}`, `${ALGOLIA_FACETS.HINGE_DISTANCE}<=${max}`]
  }

  private commonShowFacets(entries: Array<Entry> | undefined) {
    if (!Array.isArray(entries)) {
      return true
    }
    return entries.length > 1
  }

  private formatHingeDistanceFilter(rawHingeDistanceFilter: string) {
    const formattedPriceFilter = rawHingeDistanceFilter
      .replace('hingeDistance:', '')
      .replace('[', '')
      .replace(']', '')

    return formattedPriceFilter.split(ALGOLIA_FACETS.TO)
  }
  public getFacetConf(
    key: string | undefined
  ): Record<string, (p: any) => boolean | undefined> | undefined {
    if (key) {
      return Object.assign({}, this.commonFacetConf, this.facetConf[key])
    }
    return this.commonFacetConf
  }
  private showPolarized(): boolean {
    const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value
    return pageType !== 'sunglasses_polarized'
  }
  private showPrescription(): boolean {
    const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value
    return pageType !== 'prescription-sunglasses_view-all'
  }
  public inViewAllPrescription(): boolean {
    const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value
    const avoidPrescription = [
      'eyeglasses_view-all',
      'prescription-sunglasses_view-all',
      'prescription_glasses',
    ]
    return !avoidPrescription.includes(pageType)
  }

  private async getAlgoliaPromoToggles() {
    this.espotAlogliaPromo = await AlgoliaPromoToggles?.PLPPromoConfigurationEspot
  }

  private showPromofilter() {
    return this.espotAlogliaPromo?.PromoFiltersDisplayEnabled
  }
  private showPriceFacet() {
    return !wcs.isKorea() && !NoEcommerce.isStoreNoEcommerce()
  }
}

export default new AlgoliaFacetsUtils()
